import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { getItemApi, getListApi, postApi } from '../../../apiUtils';
import { ChartConfig, ReportPosition } from '../../../components/chart/Chart.types';
import { GeneralReportDataGrid } from '../../../components/datagrid/GeneralReportDataGrid';
import { Loader } from '../../../components/loader/Loader';
import { BreadcrumbValueType } from '../../../contexts/breadcrumbsContext';
import { useLoading } from '../../../contexts/loadingContext';
import { useMobileAuth } from '../../../contexts/mobileAuthContext';
import GeneralReportFilter from '../../../forms/filter/generalReport/GeneralReportFilter';
import { useIsTaskCreatePermission } from '../../../hooks/useIsTaskCreatePermission';
import { PageContentWrapper } from '../../../layouts/contentWrapper/PageContentWrapper';
import { DatagridStyle } from '../../../types/dashboard.types';
import { ReportsState } from '../../../types/locationState.types';
import { GeneralReportSetting } from '../../../types/reportSetting.types';
import { API_URL } from '../../../utils/apiUrl';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';
import MobileLayout from '../../Tasks/Mobile/components/MobileLayout';
import './Reports.scss';

export type GeneralDataType = {
  data: Array<any>;
  // filterFields: Array<any>;
  datagrid: object;
  report: {
    name: string;
    breadcrumbs: BreadcrumbValueType;
    settings: {
      id: number;
      key: string;
    };
    actions: {
      new: string;
    };
  };
};

export type GeneralReport = {
  id: number;
  name: string;
  sqlProcedure: string;
  created: string;
  createdBy?: number;
  description?: string;
  updated?: string;
  updatedBy?: number;
  visible: boolean;
};

export type GeneralReportParameter = {
  id: number;
  generalReportId: number;
  name?: string;
  type?: string;
  procedureParameterName?: string;
  defaultValue?: string;
  sourceApi?: string;
  valueName?: string;
  textName?: string;
  ordering: number;
  createdBy: number;
  created?: string;
  updatedBy: null;
  updated?: string;
};

type DataForGeneralReport = {
  data: Array<unknown>;
  datagrid: {
    columns: Array<unknown>;
    allowedPageSizes: Array<number | string>;
    filterPanel: {
      filterEnabled: boolean;
    };
    filterValue?: any;
    searchText?: string;
    pageIndex: number;
    pageSize: number;
    components: DatagridStyle;
  };
  chart?: ChartConfig;
};

interface MobileGeneralReportsParams {
  id: string;
}

export function MobileGeneralReportsTest() {
  const [report, setReport] = useState<GeneralReport | null>(null);
  const [reportParameters, setReportParameters] = useState<Array<GeneralReportParameter> | null>(
    null,
  );
  const [dataAndStructure, setDataAndStructure] = useState<DataForGeneralReport | null>(null);
  const [isFilterVisible, setFilterVisible] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [selectedFilterDatagrid, setSelectedFilterDatagrid] = useState<any | null>(null);
  const [filters, setFilters] = useState<GeneralReportSetting[]>([]);
  const { stopLoading } = useLoading();
  const { user: userMobile } = useMobileAuth();

  const { pathname } = useLocation();
  const { state }: { state: ReportsState | undefined } = useLocation();

  // Report ID. Správné načítání ID z nějakého důvodu nefunguje korektně.
  const { id } = useParams<MobileGeneralReportsParams>();

  useEffect(() => {
    if (id) {
      getReportInfo(Number(id));
      getReportParameters(Number(id));
      getFavoriteFilters(Number(id));
    }
  }, []);

  const history = useHistory();
  // Načtení práv uživatele
  const [userHasPermissionToCreate] = useIsTaskCreatePermission();
  // Reference na dataGrid. Nutné např. kvůli ukládání nastavení.
  const dataGridRef = useRef<any>(null);
  // Indikátor, zda se má provést reset filtru DataGridu i filtru mimo DataGrid
  const [resetFilterAction, setResetFilterAction] = useState<boolean>(false);

  const reportKey = `general_reports_${report?.id}`;

  /**
   * Handle editačního tlačítka v DataGridu
   * @param e  Event
   */
  const handleEditButton = (e): void => {
    if (!isNilOrEmpty(e.row) && !isNilOrEmpty(e.row.data)) {
      history.push({
        // TODO: přidat edit path
        pathname: `${''}${e.row.data.id}`,
        state: { backRoute: pathname },
      });
    }
  };

  /**
   * Funkce pro fetch dat z API dle parametrů ve fieldech / defaultních hodnot
   */
  const getReportInfo = async (id: number) => {
    await getItemApi<any>({
      url: `${API_URL.GENERAL_REPORT_ITEM}?id=${id}`,
      params: {},
      callAfterSuccess: async (result) => {
        setReport(result);
      },
    });
  };

  const getReportParameters = async (id: number) => {
    await getItemApi<any>({
      url: `${API_URL.GENERAL_REPORT_PARAMETER}?parentId=${id}`,
      params: {},
      callAfterSuccess: async (result) => {
        setReportParameters(result);
      },
    });
  };

  /**
   * Fetch všech filtrů daného typu z API
   */
  const getFavoriteFilters = async (id: number) => {
    if (!userMobile) return;
    console.log('userMobile', userMobile);
    await getListApi<Array<GeneralReportSetting>>({
      url: `${API_URL.GENERAL_REPORT_SETTING_LIST_FOR_USER}`,
      params: {
        userId: userMobile?.personalNumber,
        reportId: id,
      },
      callAfterSuccess: (result: Array<GeneralReportSetting>) => {
        setFilters(result);
      },
    });
  };

  /**
   * Funkce pro fetch dat z API dle parametrů ve fieldech / defaultních hodnot
   */
  const onParamsValueChange = async (params = {}) => {
    const response = await postApi<DataForGeneralReport>({
      url: `${API_URL.GENERAL_REPORT_DATA}?reportId=${id}`,
      data: params,
      hideSuccessNotifications: true,
    });
    setDataAndStructure(response ?? null);
    stopLoading();
  };

  if (!report && !reportParameters) return <Loader />;

  console.log('reportParameters', reportParameters);
  console.log('filters', filters);

  return (
    <PageContentWrapper noContentBlock noDxCard noResponsivePaddings>
      <MobileLayout>
        <div style={{ padding: 10 }}>
          {reportParameters && report && (
            <GeneralReportFilter
              dataGridRef={dataGridRef}
              onParamsValueChange={onParamsValueChange}
              report={report}
              onFilterChange={(name: string | null, dataGrid: any | null) => {
                setSelectedFilter(name);
                setSelectedFilterDatagrid(dataGrid);
              }}
              selectedFilter={selectedFilter}
              filterVisibility={isFilterVisible}
              loadDefaultSetting={!state?.loadSettingFromSession}
              storageKey={reportKey}
              transFormName={''}
              filterFields={reportParameters}
              isDataPresent={dataAndStructure !== null}
              heading={`${report?.name}`}
              setSelectedFilter={setSelectedFilter}
              paramPosition={4}
              platform="mobile"
              datagridId={`datagrid_report_mobile_${report?.id}`}
            />
          )}
          {dataAndStructure && (
            <GeneralReportDataGrid
              dataGridRef={dataGridRef}
              selectedFilterDatagrid={selectedFilterDatagrid}
              data={null}
              dataAndStructure={dataAndStructure}
              resetFilter={() => setResetFilterAction(true)}
              userCanCreate={userHasPermissionToCreate}
              handleEdit={handleEditButton}
              filterVisibility={isFilterVisible}
              setFilterVisibility={setFilterVisible}
              transFormName={''}
              newEntryActionPath={''}
              datagridNoResponsiveClassName={
                dataAndStructure && dataAndStructure.chart?.reportPosition === ReportPosition.RIGHT
                  ? 'left'
                  : ''
              }
              disableDatagridNoResponsive
              enableGrouping={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableGrouping !== undefined
                  ? dataAndStructure.datagrid.components.enableGrouping
                  : true
              }
              enableXlsxExport={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableXlsxExport !== undefined
                  ? dataAndStructure.datagrid.components.enableXlsxExport
                  : true
              }
              enableColumnChooser={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableColumnChooser !== undefined
                  ? dataAndStructure.datagrid.components.enableColumnChooser
                  : true
              }
              enableHeaderFiltering={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableHeaderFiltering !== undefined
                  ? dataAndStructure.datagrid.components.enableHeaderFiltering
                  : true
              }
              enableRowFiltering={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableRowFiltering !== undefined
                  ? dataAndStructure.datagrid.components.enableRowFiltering
                  : true
              }
              enableSearchPanel={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableSearchPanel !== undefined
                  ? dataAndStructure.datagrid.components.enableSearchPanel
                  : true
              }
              enableColumnReordering={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableColumnReordering !== undefined
                  ? dataAndStructure.datagrid.components.enableColumnReordering
                  : true
              }
              enableColumnResizing={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.enableColumnResizing !== undefined
                  ? dataAndStructure.datagrid.components.enableColumnResizing
                  : true
              }
              rowAlternationEnabled={
                dataAndStructure.datagrid.components &&
                dataAndStructure.datagrid.components.rowAlternationEnabled !== undefined
                  ? dataAndStructure.datagrid.components.rowAlternationEnabled
                  : true
              }
              enableResetFilter={false}
              enableFilterToggle={
                !!(
                  (filters && filters.length > 1) ||
                  (reportParameters && reportParameters.length > 0)
                )
              }
              datagridId={`datagrid_report_mobile_${report?.id}`}
            />
          )}
        </div>
      </MobileLayout>
    </PageContentWrapper>
  );
}
export default GeneralReport;
